import store from '@/store';
import { sendNotification, verifyOverlap } from './utils';
import { updateSessionContentType } from './amplify';
import { isContentTypeAvailable, isHorseAvailable, typeToFrench, busyContentTypes, checkHorseDailyEffort } from './dataManager';
import { getSessionFieldByWtype } from './sessions';

const dropBySessionType = require('../configs/dragAndDropLimitations.json');

const isSession = contentType => contentType && contentType.wtype && contentType.wtype === 'session';

const dragStart = (event, contentType) => {
  if (contentType) {
    event.dataTransfer.setData('type', contentType.wtype);
    event.dataTransfer.setData(contentType.wtype, contentType.uuid);
  }
};

const verifyDragAndDropExceptions = (session, typeToCompare) => {
  if (Object.keys(dropBySessionType).includes(session.type)) {
    const type = dropBySessionType[session.type];

    if (type.droppableContentType && type.droppableContentType.includes('*')) {
      return true;
    }

    if (type.droppableContentType && !type.droppableContentType.includes(typeToCompare)) {
      return false;
    }
    return true;
  }
  return true;
};

const doesSessionAllowDrop = (session, typeToCompare) => {
  if (Object.keys(dropBySessionType).includes(session.type)) {
    const type = dropBySessionType[session.type];
    if (type.droppableContentType && type.droppableContentType.includes('*')) {
      return true;
    }

    if (type.droppableContentType && !type.droppableContentType.includes(typeToCompare)) {
      return false;
    }
    return true;
  }
  return true;
};

const canDrop = (draggedContentType, droppedContentType) => {
  if (!draggedContentType) {
    const text = 'Merci de prévoir une séance';
    sendNotification('error', text);
    return false;
  }

  const isContentTypeSession = isSession(droppedContentType);
  const session = isContentTypeSession ? droppedContentType : draggedContentType;

  const contentType = isContentTypeSession ? draggedContentType : droppedContentType;

  const canDropOnContentType = doesSessionAllowDrop(session, contentType.wtype);

  if (!canDropOnContentType) {
    const text = 'Le type de session ne reçoit pas cette ressource';
    sendNotification('error', text);
    return false;
  }

  if (draggedContentType.wtype === droppedContentType.wtype) {
    const text = 'Impossible de glisser-déposer pour le même type';
    sendNotification('error', text);
    return false;
  }

  if (session.cancelled) {
    const text = 'la session est annulée';
    sendNotification('error', text);
    return false;
  }

  const contentTypeAvailable = isContentTypeAvailable(contentType, session);

  if (!contentTypeAvailable) {
    const text = `${contentType.wtitle} n'est pas disponible`;
    sendNotification('error', text);
    return false;
  }

  const wtypesToVerifyBusy = {
    horse: {
      field: 'horse_name',
      frenchLabel: 'Cheval'
    }
  };

  // horse movements entre sortie
  if (contentType.wtype === 'horse') {
    const horseAvailable = isHorseAvailable(contentType);

    if (!horseAvailable) {
      const text = 'Le cheval a été déplacé vers un autre endroit';
      sendNotification('error', text);
      return false;
    }
  }

  if (wtypesToVerifyBusy.hasOwnProperty(contentType.wtype)) {
    const value = wtypesToVerifyBusy[contentType.wtype];
    const contentBusyInInterval = busyContentTypes(getSessionFieldByWtype(contentType.wtype), session);

    if (contentBusyInInterval.some(ct => ct.uuid === contentType.uuid)) {
      const text = `${value.frenchLabel} ${contentType[value.field]} déjà utilisé dans une autre séance`;
      sendNotification('error', text);
      return false;
    }
  }

  const { sessions } = store.state;

  const sessionField = getSessionFieldByWtype(contentType.wtype);

  const isOverlap = verifyOverlap(contentType, session, sessionField, sessions, false);

  if (contentType.wtype === 'place') {
    return true;
  }

  if (isOverlap) {
    // const text = `The ${type} already have a session in the same time`;
    const text = `Le ${typeToFrench(draggedContentType.wtype)} a déjà une séance en même temps`;
    sendNotification('error', text);
    return false;
  }

  return true;
};

const allowMultipleContentType = wtype => {
  switch (wtype) {
    case 'horse':
    case 'collaborator':
    case 'instructor':
      return true;
    default:
      return false;
  }
};

const drop = async (event, contentType) => {
  const draggedType = event.dataTransfer.getData('type');
  const draggedUuid = event.dataTransfer.getData(draggedType);

  const storeField = draggedType === 'collaborator' ? 'instructor' : draggedType;

  const draggedContentType = store.getters[storeField](draggedUuid);

  const isContentTypeSession = isSession(contentType);

  // style
  const draggedObject = document.getElementById(contentType.uuid);
  const color = isContentTypeSession ? contentType.color : 'white';
  draggedObject.style.backgroundColor = color;

  // validation
  const isDropPossible = canDrop(draggedContentType, contentType);

  if (!isDropPossible) {
    return;
  }

  const uuid = isContentTypeSession ? event.dataTransfer.getData(draggedType) : contentType.uuid;
  const session = isContentTypeSession ? contentType : draggedContentType;

  let storeFieldToSearch = draggedType === 'collaborator' ? 'instructors' : `${draggedType}s`;

  if (!isContentTypeSession) {
    switch (contentType.wtype) {
      case 'collaborator':
        storeFieldToSearch = 'instructors';
        break;
      default:
        storeFieldToSearch = `${contentType.wtype}s`;
    }
  }
  const contentTypeArray = store.state[storeFieldToSearch];
  const wtype = isContentTypeSession ? draggedType : contentType.wtype;
  const sessionField = getSessionFieldByWtype(wtype);
  const sessionAllowMultipleContentType = allowMultipleContentType(wtype);

  if (wtype === 'horse') {
    const horse = store.getters.horse(uuid);
    const checkForHorseEffort = await checkHorseDailyEffort([horse], [session]);
    if (Object.keys(checkForHorseEffort).length) {
      store.commit('setAlertEffort', checkForHorseEffort);
      store.commit('setShowMaxEffortModal', true);
      store.commit('setMaxEffortCallback', () => updateSessionContentType(uuid, session, contentTypeArray, sessionField, sessionAllowMultipleContentType, wtype));
      return;
    }
  }

  updateSessionContentType(uuid, session, contentTypeArray, sessionField, sessionAllowMultipleContentType, wtype);
};

const handleDrop = (event, contentType) => {
  const draggedType = event.dataTransfer.getData('type');
  const draggedUuid = event.dataTransfer.getData(draggedType);

  const storeField = draggedType === 'collaborator' ? 'instructor' : draggedType;

  const draggedContentType = store.getters[storeField](draggedUuid);

  const isContentTypeSession = isSession(contentType);

  // style
  const draggedObject = document.getElementById(contentType.uuid);

  if (draggedObject) {
    const color = isContentTypeSession ? contentType.color : 'white';
    draggedObject.style.backgroundColor = color;
  }

  const { canUserPerformAction } = store.getters;

  if (!canUserPerformAction) {
    sendNotification('error', 'L\'utilisateur a uniquement l\'autorisation de préparer');
    return { isCustomerCase: false };
  }

  const sessionToSend = isContentTypeSession ? contentType : draggedContentType;

  const isDropPossible = canDrop(draggedContentType, contentType);

  if (!isDropPossible) {
    // sendNotification('error', 'peux pas déposer l\'élément dans cette session');
    return { isCustomerCase: false, session: sessionToSend };
  }

  if (draggedContentType.wtype === 'customer' || contentType.wtype === 'customer') {
    const customer = draggedContentType.wtype === 'customer' ? draggedContentType : contentType;

    return { isCustomerCase: true, customer_uuid: customer.uuid, session: sessionToSend, customer: customer };
  }

  drop(event, contentType);
  return { isCustomerCase: false, session: sessionToSend };
};

const dragLeave = (event, contentType) => {
  if (contentType) {
    const draggedObject = document.getElementById(contentType.uuid);
    if (draggedObject) {
      const color = isSession(contentType) ? contentType.color : 'white';
      draggedObject.style.backgroundColor = color;
    }
  }
};

const dragOver = (event, contentType) => {
  event.preventDefault();
  if (contentType) {
    const draggedObject = document.getElementById(contentType.uuid);
    if (draggedObject) {
      draggedObject.style.backgroundColor = 'red';
    }
  }
};

export { handleDrop, dragStart, drop, dragLeave, dragOver, verifyDragAndDropExceptions, isSession };
